import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

/**
 * Theme (dark / light) with a class-based switch.
 *
 * The active theme toggles the `.dark` class on <html>; all colour comes from
 * the semantic tokens in index.css, so the whole app switches at once.
 *
 * Persisted to localStorage; first visit follows the OS preference, falling
 * back to dark (the app's original look). `applyStoredTheme()` is called once
 * before React renders (in main.tsx) to set the class pre-paint and avoid a flash.
 */

export type Theme = 'dark' | 'light'

const STORAGE_KEY = 'stride.theme'

/** Resolve the theme to use: stored choice → OS preference → dark. */
export function resolveInitialTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'dark' || stored === 'light') return stored
  } catch {
    /* localStorage unavailable — fall through */
  }
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: light)').matches) {
    return 'light'
  }
  return 'dark'
}

/** Apply (or remove) the `.dark` class on <html>. */
function applyThemeClass(theme: Theme): void {
  const root = document.documentElement
  root.classList.toggle('dark', theme === 'dark')
}

/** Set the theme class before React renders — call once from main.tsx. */
export function applyStoredTheme(): void {
  applyThemeClass(resolveInitialTheme())
}

interface ThemeContextValue {
  theme:       Theme
  setTheme:    (t: Theme) => void
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

export function ThemeProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [theme, setThemeState] = useState<Theme>(resolveInitialTheme)

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t)
    try { localStorage.setItem(STORAGE_KEY, t) } catch { /* ignore */ }
  }, [])

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const next: Theme = prev === 'dark' ? 'light' : 'dark'
      try { localStorage.setItem(STORAGE_KEY, next) } catch { /* ignore */ }
      return next
    })
  }, [])

  // Keep the <html> class in sync whenever the theme changes.
  useEffect(() => { applyThemeClass(theme) }, [theme])

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider')
  return ctx
}
